import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {UpcomingEvent} from 'api/events/events.types';
import {
    determineInitialState,
    OptOutWizardFlowType,
    getWizardSteps,
} from '../utils/utils';

const OPT_OUT_RADIO_OPTIONS = {
    CONTINUE_SELLING: 'CONTINUE_SELLING',
    SET_BLACKOUT: 'SET_BLACKOUT',
} as const;

export {OPT_OUT_RADIO_OPTIONS};
export type OptOutRadioOptions =
    (typeof OPT_OUT_RADIO_OPTIONS)[keyof typeof OPT_OUT_RADIO_OPTIONS];

export interface EventOptOutWizardState {
    isLoading: boolean;
    error: boolean;
    currentStep: number;
    steps: string[];
    flow: OptOutWizardFlowType | null;
    event: UpcomingEvent | null;
    optOutOption: OptOutRadioOptions;
}

const initialState: EventOptOutWizardState = {
    isLoading: false,
    error: false,
    currentStep: 0,
    steps: [],
    flow: null,
    event: null,
    optOutOption: OPT_OUT_RADIO_OPTIONS.CONTINUE_SELLING,
};

/**
 * Redux slice for managing the state of the event opt-out wizard.
 */
const eventOptOutWizardSlice = createSlice({
    name: 'events/event-opt-out-wizard',
    initialState,
    reducers: {
        initializeWizard: (
            state,
            action: PayloadAction<{
                event: UpcomingEvent;
                flow: OptOutWizardFlowType;
            }>
        ) => {
            const {event, flow} = action.payload;
            const wizardState = determineInitialState(event, flow);

            state.steps = getWizardSteps(wizardState);
            state.currentStep = 0;
            state.flow = flow;
            state.event = event;
            state.isLoading = false;
            state.error = false;
        },
        nextStep: (state) => {
            if (state.currentStep < state.steps.length - 1) {
                state.currentStep += 1;
            }
        },
        prevStep: (state) => {
            if (state.currentStep > 0) {
                state.currentStep -= 1;
            }
        },
        setStep: (state, action: PayloadAction<number>) => {
            if (action.payload >= 0 && action.payload < state.steps.length) {
                state.currentStep = action.payload;
            }
        },
        resetWizard: () => {
            return initialState;
        },
        setWizardFlow: (state, action: PayloadAction<OptOutWizardFlowType>) => {
            state.flow = action.payload;
        },
        setOptOutOption: (state, action: PayloadAction<OptOutRadioOptions>) => {
            state.optOutOption = action.payload;
        },
    },
});

export const {
    initializeWizard,
    nextStep,
    prevStep,
    setStep,
    setWizardFlow,
    resetWizard,
    setOptOutOption,
} = eventOptOutWizardSlice.actions;

export default eventOptOutWizardSlice.reducer;
